import { useEffect, useRef, useState } from 'react';
import { Languages, RefreshCw } from 'lucide-react';
// Relative imports (not `@/`) on purpose — see useTranslations.ts note.
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';
import { ConfirmDialog } from '../../../components/dialogs/ConfirmDialog';
import { useToast } from '../../../components/ui/use-toast';
import {
  useRetryMissingTranslations,
  useTranslationSummary,
} from '../../../hooks/api/useTranslations';
import type { TranslationSummaryEntry } from '../../../api/translations';

/**
 * TELV1-503 — ops-facing transcript-translation coverage + forced retry.
 *
 * Born from the TELV1-501 incident, where backfilling 12 customer
 * projects required raw authenticated fetch calls because the proxy
 * endpoints had no UI. This card makes translation gaps visible per
 * language and lets ops re-queue the missing ones without engineering.
 *
 * Completion detection (architect review F3): the summary's `total` is
 * live — on an actively-fielding project it grows while a retry runs, so
 * absolute `translated === total` may never hold and would fire a false
 * "check Sentry" alarm about fresh arrivals. We therefore capture the
 * total AT RETRY START; the forced batch only targets conversations that
 * existed then, so `translated >= totalAtStart` is the honest completion
 * signal, and the timeout only fires when the original missing set truly
 * did not fill.
 */
const RETRY_TIMEOUT_MS = 15 * 60 * 1000;

interface RetryWatch {
  language: string;
  totalAtStart: number;
  startedAt: number;
}

interface Props {
  projectId: string;
}

function TranslationRow({
  entry,
  retrying,
  retryDisabled,
  onRetry,
}: {
  entry: TranslationSummaryEntry;
  retrying: boolean;
  retryDisabled: boolean;
  onRetry: () => void;
}) {
  const missing = Math.max(0, entry.total - entry.translated);
  const complete = missing === 0;
  const pct = entry.total > 0 ? (entry.translated / entry.total) * 100 : 100;

  return (
    <div
      data-testid={`translation-row-${entry.language}`}
      className={`rounded-md border p-3 space-y-2 ${
        complete ? 'border-border' : 'border-amber-300 bg-amber-50 dark:bg-amber-950/20'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm">{entry.language}</span>
        <span className="text-sm text-muted-foreground">
          {entry.translated} / {entry.total}
          {!complete && (
            <span className="ml-2 font-medium text-amber-700 dark:text-amber-400">
              {missing} missing
            </span>
          )}
        </span>
      </div>
      <Progress value={pct} />
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-xs text-muted-foreground"
          title="Conversations with a recorded translation failure. Cleared on later success, but records from before Jul 2026 can persist after healing — a hint, not a live gap count."
        >
          {entry.failed > 0
            ? `${entry.failed} recorded failure${entry.failed === 1 ? '' : 's'}`
            : ''}
        </span>
        {!complete && (
          <Button size="sm" variant="outline" disabled={retryDisabled} onClick={onRetry}>
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying…' : 'Retry missing'}
          </Button>
        )}
      </div>
    </div>
  );
}

export function TranslationsCard({ projectId }: Props) {
  const { toast } = useToast();
  const [watch, setWatch] = useState<RetryWatch | null>(null);
  const [confirmLanguage, setConfirmLanguage] = useState<string | null>(null);
  const retryMissing = useRetryMissingTranslations();
  const { data: summary, isLoading } = useTranslationSummary(projectId, !!watch);
  // The completion effect reads the latest watch without re-arming on
  // every poll tick; state drives rendering, the ref drives the check.
  const watchRef = useRef<RetryWatch | null>(null);
  watchRef.current = watch;

  useEffect(() => {
    const active = watchRef.current;
    if (!active || !summary) return;
    const entry = summary.find((s) => s.language === active.language);
    if (!entry) return;

    if (entry.translated >= active.totalAtStart) {
      toast({
        title: 'Translations complete',
        description: `${active.language}: ${entry.translated} / ${entry.total} conversations translated.`,
      });
      setWatch(null);
      return;
    }
    if (Date.now() - active.startedAt >= RETRY_TIMEOUT_MS) {
      toast({
        title: 'Retry still incomplete',
        description: `${active.language} is at ${entry.translated} / ${entry.total} after 15 minutes — the remaining conversations may be failing. Check Sentry (pipeline_stage:translation).`,
        variant: 'destructive',
      });
      setWatch(null);
    }
  }, [summary, toast]);

  const handleRetry = (language: string) => {
    const entry = summary?.find((s) => s.language === language);
    if (!entry) return;
    // Watch is set BEFORE the mutation on purpose (PR review): the fast
    // poll must be armed from the first tick, and totalAtStart must
    // snapshot the pre-retry summary. onError below rolls it back.
    setWatch({ language, totalAtStart: entry.total, startedAt: Date.now() });
    retryMissing.mutate(
      { projectId, language },
      {
        onError: () => setWatch(null),
      },
    );
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Translations</h3>
        <Languages className="h-5 w-5 text-muted-foreground" />
      </div>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {!isLoading && !summary?.length && (
        <p className="text-sm text-muted-foreground">No translations requested for this project.</p>
      )}
      {!isLoading && !!summary?.length && (
        <div className="space-y-4">
          {summary.map((entry) => (
            <TranslationRow
              key={entry.language}
              entry={entry}
              retrying={watch?.language === entry.language}
              retryDisabled={!!watch || retryMissing.isPending}
              onRetry={() => setConfirmLanguage(entry.language)}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmLanguage}
        onOpenChange={(open) => !open && setConfirmLanguage(null)}
        title={`Retry missing ${confirmLanguage ?? ''} translations?`}
        description="Re-queues only the conversations that have no translation yet (existing translations are never redone). Bypasses the automatic retry cap, so use after fixing the underlying issue or when conversations look stuck."
        confirmLabel="Retry missing"
        onConfirm={() => {
          if (confirmLanguage) handleRetry(confirmLanguage);
          setConfirmLanguage(null);
        }}
      />
    </div>
  );
}
